const TOUR_STORAGE_PREFIX = 'corepay:tour:payroll:';

function qs(selector, root = document) {
    try {
        return root.querySelector(selector);
    } catch (_) {
        return null;
    }
}

function resolveTourTarget(step) {
    const selectors = step.selectors?.length
        ? step.selectors
        : (step.selector ? [step.selector] : []);

    for (const sel of selectors) {
        const candidate = qs(sel);
        if (!candidate) {
            continue;
        }

        forceExpandSidebarAncestors(candidate);

        if (isVisible(candidate)) {
            return candidate;
        }
    }

    return null;
}

function isVisible(el) {
    if (!el) return false;
    const rect = el.getBoundingClientRect();
    const style = window.getComputedStyle(el);
    return (
        style.visibility !== 'hidden' &&
        style.display !== 'none' &&
        rect.width > 0 &&
        rect.height > 0
    );
}

/** Open parent collapse panels so nested sidebar links are visible for the tour. */
function forceExpandSidebarAncestors(el) {
    if (!el || typeof el.closest !== 'function') return;
    if (!el.closest('.sidenav-menu')) return;
    let n = el;
    while (n) {
        if (n.classList?.contains('collapse')) {
            n.classList.add('show');
            const toggle = n.previousElementSibling;
            if (toggle?.matches?.('a[data-bs-toggle="collapse"]')) {
                toggle.setAttribute('aria-expanded', 'true');
            }
        }
        n = n.parentElement;
    }
}

const SPOTLIGHT_PAD_PX = 8;

function ensureBackdrop() {
    let backdrop = document.getElementById('cp-tour-backdrop');
    if (!backdrop) {
        backdrop = document.createElement('div');
        backdrop.id = 'cp-tour-backdrop';
        backdrop.className = 'cp-tour-backdrop';
        backdrop.setAttribute('aria-hidden', 'true');
        ['top', 'right', 'bottom', 'left'].forEach((side) => {
            const panel = document.createElement('div');
            panel.className = `cp-tour-scrim cp-tour-scrim--${side}`;
            panel.setAttribute('aria-hidden', 'true');
            backdrop.appendChild(panel);
        });
        document.body.appendChild(backdrop);
    }
    backdrop.classList.add('is-active');
    return backdrop;
}

function removeBackdrop() {
    const backdrop = document.getElementById('cp-tour-backdrop');
    backdrop?.classList.remove('is-active');
}

/** Dim + blur only outside the target rect (four fixed panels with a “hole”). */
function syncSpotlightPanels(targetEl) {
    const backdrop = document.getElementById('cp-tour-backdrop');
    const topEl = backdrop?.querySelector('.cp-tour-scrim--top');
    const rightEl = backdrop?.querySelector('.cp-tour-scrim--right');
    const bottomEl = backdrop?.querySelector('.cp-tour-scrim--bottom');
    const leftEl = backdrop?.querySelector('.cp-tour-scrim--left');
    if (!backdrop?.classList.contains('is-active') || !topEl || !rightEl || !bottomEl || !leftEl) {
        return;
    }

    const fullBleed = () => {
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        topEl.style.cssText = `left:0;top:0;width:${vw}px;height:${vh}px;`;
        leftEl.style.cssText = 'display:none;';
        rightEl.style.cssText = 'display:none;';
        bottomEl.style.cssText = 'display:none;';
    };

    if (!targetEl || typeof targetEl.getBoundingClientRect !== 'function') {
        fullBleed();
        return;
    }

    const r = targetEl.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const pad = SPOTLIGHT_PAD_PX;

    let left = r.left - pad;
    let top = r.top - pad;
    let width = r.width + pad * 2;
    let height = r.height + pad * 2;

    if (left < 0) {
        width += left;
        left = 0;
    }
    if (top < 0) {
        height += top;
        top = 0;
    }
    if (left + width > vw) {
        width = Math.max(0, vw - left);
    }
    if (top + height > vh) {
        height = Math.max(0, vh - top);
    }

    if (width <= 2 || height <= 2) {
        fullBleed();
        return;
    }

    leftEl.style.display = '';
    rightEl.style.display = '';
    bottomEl.style.display = '';

    topEl.style.cssText = `left:0;top:0;width:${vw}px;height:${Math.max(0, top)}px;`;
    leftEl.style.cssText = `left:0;top:${top}px;width:${Math.max(0, left)}px;height:${height}px;`;
    rightEl.style.cssText = `left:${left + width}px;top:${top}px;width:${Math.max(0, vw - left - width)}px;height:${height}px;`;
    bottomEl.style.cssText = `left:0;top:${top + height}px;width:${vw}px;height:${Math.max(0, vh - top - height)}px;`;
}

function scrollToElement(el) {
    try {
        el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
    } catch (_) {
        // ignore
    }
}

function setHighlighted(el) {
    document.querySelectorAll('.cp-tour-highlight').forEach(n => n.classList.remove('cp-tour-highlight'));
    if (el) el.classList.add('cp-tour-highlight');
}

function hideActivePopover() {
    const active = document.querySelector('[data-cp-tour-popover="true"]');
    if (active) {
        try {
            bootstrap.Popover.getInstance(active)?.dispose();
        } catch (_) {
            // ignore
        }
        active.removeAttribute('data-cp-tour-popover');
    }
}

function setTourComplete(tourId) {
    try {
        localStorage.setItem(`${TOUR_STORAGE_PREFIX}${tourId}`, 'completed');
    } catch (_) {
        // ignore
    }
}

function escapeHtml(str) {
    return String(str ?? '')
        .replaceAll('&', '&amp;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;')
        .replaceAll('"', '&quot;')
        .replaceAll("'", '&#039;');
}

/** Allow normal navigation targets only (tour “Open this page” CTA). */
function isSafeTourNavigationHref(href) {
    if (href == null || typeof href !== 'string') return false;
    const h = href.trim();
    if (!h) return false;
    const lower = h.toLowerCase();
    if (lower.startsWith('javascript:') || lower.startsWith('data:') || lower.startsWith('vbscript:')) {
        return false;
    }
    if (h === '#' || h.startsWith('#')) {
        return false;
    }
    return true;
}

/** If the highlighted element (or an ancestor) is a link, offer “Open this page”. */
function findTourOpenLinkFromElement(el, preferredLabel) {
    if (!el || typeof el.closest !== 'function') return null;
    const a = el.matches?.('a[href]') ? el : el.closest('a[href]');
    if (!a) return null;
    const href = a.getAttribute('href');
    if (!isSafeTourNavigationHref(href)) return null;
    return { href, label: preferredLabel || 'Open this page' };
}

function tourIllustration(name) {
    const common = 'width="140" height="82" viewBox="0 0 140 82" fill="none" xmlns="http://www.w3.org/2000/svg"';
    const card = '<rect x="6" y="8" width="128" height="66" rx="12" fill="rgba(32,107,196,0.10)" stroke="rgba(32,107,196,0.25)"/>';
    const lines = '<rect x="18" y="22" width="62" height="8" rx="4" fill="rgba(15,23,42,0.35)"/><rect x="18" y="36" width="86" height="8" rx="4" fill="rgba(15,23,42,0.22)"/><rect x="18" y="50" width="52" height="8" rx="4" fill="rgba(15,23,42,0.18)"/>';
    const badge = '<rect x="92" y="22" width="30" height="16" rx="8" fill="rgba(47,179,68,0.18)" stroke="rgba(47,179,68,0.32)"/><circle cx="101" cy="30" r="3" fill="rgba(47,179,68,0.85)"/>';
    const chart = '<path d="M22 56 C36 44, 52 52, 66 38 C78 28, 92 36, 110 24" stroke="rgba(32,107,196,0.95)" stroke-width="3" stroke-linecap="round"/><circle cx="22" cy="56" r="3.5" fill="rgba(32,107,196,0.95)"/><circle cx="66" cy="38" r="3.5" fill="rgba(32,107,196,0.95)"/><circle cx="110" cy="24" r="3.5" fill="rgba(32,107,196,0.95)"/>';
    const menu = '<rect x="16" y="18" width="34" height="46" rx="10" fill="rgba(15,23,42,0.06)" stroke="rgba(15,23,42,0.14)"/><rect x="22" y="26" width="22" height="6" rx="3" fill="rgba(15,23,42,0.30)"/><rect x="22" y="38" width="18" height="6" rx="3" fill="rgba(15,23,42,0.20)"/><rect x="22" y="50" width="24" height="6" rx="3" fill="rgba(15,23,42,0.16)"/>';
    const focus = '<rect x="60" y="20" width="66" height="44" rx="12" fill="rgba(32,107,196,0.06)" stroke="rgba(32,107,196,0.20)"/><rect x="70" y="30" width="46" height="8" rx="4" fill="rgba(15,23,42,0.22)"/><rect x="70" y="44" width="30" height="8" rx="4" fill="rgba(15,23,42,0.16)"/><rect x="102" y="44" width="14" height="8" rx="4" fill="rgba(47,179,68,0.18)" stroke="rgba(47,179,68,0.32)"/>';

    if (name === 'sidebar') return `<svg ${common}>${menu}${focus}</svg>`;
    if (name === 'dashboard') return `<svg ${common}>${card}${chart}${badge}</svg>`;
    if (name === 'runs') return `<svg ${common}>${card}${lines}${badge}</svg>`;
    if (name === 'settings') return `<svg ${common}>${card}<circle cx="92" cy="41" r="16" fill="rgba(15,23,42,0.06)" stroke="rgba(15,23,42,0.18)"/><path d="M92 30v22M81 41h22" stroke="rgba(32,107,196,0.9)" stroke-width="3" stroke-linecap="round"/></svg>`;
    return `<svg ${common}>${card}${lines}</svg>`;
}

function buildPopoverContent({
    title,
    body,
    stepIndex,
    totalSteps,
    tip,
    openLinkHref,
    openLinkLabel,
    isFirstStep,
}) {
    const current = stepIndex + 1;
    const pct = Math.round((current / Math.max(totalSteps, 1)) * 100);
    const safeTitle = escapeHtml(title ?? 'Tour');
    const isLast = current >= totalSteps;
    const nextLabel = isLast ? 'Finish' : 'Next';
    const safeTip = tip ? escapeHtml(tip) : '';
    const safeOpenHref = openLinkHref ? escapeHtml(openLinkHref) : '';
    const safeOpenLabel = escapeHtml(openLinkLabel || 'Open this page');

    const introHtml =
        isFirstStep
            ? `<p class="cp-tour-popover__intro">Walk through the payroll sidebar, then this page. Press <span class="cp-tour-kbd">Esc</span> to exit anytime.</p>`
            : '';

    const tipHtml = safeTip
        ? `<div class="cp-tour-popover__tip"><strong>Tip:</strong> ${safeTip}</div>`
        : '';

    const openHtml =
        safeOpenHref && isSafeTourNavigationHref(openLinkHref)
            ? `<a href="${safeOpenHref}" class="cp-tour-popover__open-link" data-cp-tour-open-link="1">${safeOpenLabel}</a>`
            : '';

    const backBtn = stepIndex > 0
        ? `<button type="button" class="btn btn-light btn-sm cp-tour-btn-back" data-cp-tour-action="prev">Back</button>`
        : '';

    return `
        <div class="cp-tour-popover">
            <div class="cp-tour-popover__top">
                <span class="cp-tour-popover__step">Step ${current} of ${totalSteps}</span>
                <button type="button" class="btn-close btn-close-sm cp-tour-btn-skip" data-cp-tour-action="skip" aria-label="Close tour"></button>
            </div>
            <div class="cp-tour-popover__progress" role="progressbar" aria-valuenow="${pct}" aria-valuemin="0" aria-valuemax="100">
                <div class="cp-tour-popover__progress-fill" style="width:${pct}%"></div>
            </div>
            <h3 class="cp-tour-popover__title">${safeTitle}</h3>
            ${introHtml}
            <p class="cp-tour-popover__body">${body ?? ''}</p>
            ${tipHtml}
            ${openHtml}
            <div class="cp-tour-popover__actions">
                ${backBtn}
                <div class="cp-tour-popover__spacer"></div>
                <button type="button" class="btn btn-primary btn-sm cp-tour-btn-next" data-cp-tour-action="next">${escapeHtml(nextLabel)}</button>
            </div>
        </div>
    `;
}

function createTour(tourId, steps) {
    let idx = 0;
    let cleanupFns = [];
    let spotlightTargetEl = null;

    const syncSpotlight = () => {
        syncSpotlightPanels(spotlightTargetEl);
    };

    const cleanup = () => {
        cleanupFns.forEach(fn => {
            try {
                fn();
            } catch (_) {
                // ignore
            }
        });
        cleanupFns = [];
        hideActivePopover();
        setHighlighted(null);
        spotlightTargetEl = null;
        removeBackdrop();
        document.body.classList.remove('cp-tour-active');
    };

    const clearStepHandlers = () => {
        cleanupFns.forEach(fn => {
            try {
                fn();
            } catch (_) {
                // ignore
            }
        });
        cleanupFns = [];
    };

    const showStep = (nextIdx) => {
        clearStepHandlers();
        const total = steps.length;
        const bounded = Math.max(0, Math.min(nextIdx, total - 1));
        idx = bounded;

        const step = steps[idx];
        if (!step) return;

        hideActivePopover();
        ensureBackdrop();
        document.body.classList.add('cp-tour-active');
        spotlightTargetEl = null;
        syncSpotlightPanels(null);

        let el = resolveTourTarget(step);

        const skipOrEnd = () => {
            if (idx < total - 1) return showStep(idx + 1);
            cleanup();
            setTourComplete(tourId);
        };

        const presentStep = () => {
            el = resolveTourTarget(step);
            if (!el || !isVisible(el)) {
                return skipOrEnd();
            }

            scrollToElement(el);
            setHighlighted(el);
            spotlightTargetEl = el;
            syncSpotlight();
            requestAnimationFrame(syncSpotlight);
            window.setTimeout(syncSpotlight, 50);
            window.setTimeout(syncSpotlight, 400);

            const onScrollOrResize = () => syncSpotlight();
            document.addEventListener('scroll', onScrollOrResize, true);
            window.addEventListener('resize', onScrollOrResize);
            cleanupFns.push(() => document.removeEventListener('scroll', onScrollOrResize, true));
            cleanupFns.push(() => window.removeEventListener('resize', onScrollOrResize));

            el.setAttribute('data-cp-tour-popover', 'true');

            let openFromTarget = null;
            if (step.openLink !== false) {
                openFromTarget = findTourOpenLinkFromElement(el, step.openLinkLabel);
            }

            const pop = new bootstrap.Popover(el, {
                trigger: 'manual',
                placement: step.placement ?? 'auto',
                html: true,
                sanitize: false,
                container: 'body',
                title: '',
            content: buildPopoverContent({
                    title: step.title,
                    body: step.body,
                    stepIndex: idx,
                    totalSteps: total,
                    tip: step.tip,
                    openLinkHref: openFromTarget?.href ?? null,
                    openLinkLabel: openFromTarget?.label ?? null,
                    isFirstStep: idx === 0,
                }),
                template: `
                <div class="popover cp-tour-popover-shell" role="tooltip">
                    <div class="popover-arrow"></div>
                    <div class="popover-body"></div>
                </div>
            `,
            });
            pop.show();

            const onDocClick = (e) => {
                const tourLink = e.target?.closest?.('a[data-cp-tour-open-link]');
                if (
                    tourLink &&
                    e.button === 0 &&
                    !e.metaKey &&
                    !e.ctrlKey &&
                    !e.shiftKey
                ) {
                    const url = tourLink.getAttribute('href');
                    if (url && isSafeTourNavigationHref(url)) {
                        e.preventDefault();
                        cleanup();
                        setTourComplete(tourId);
                        window.location.assign(url);
                    }
                    return;
                }

                const actionEl = e.target?.closest?.('[data-cp-tour-action]');
                if (!actionEl) return;

                const action = actionEl.getAttribute('data-cp-tour-action');
                if (action === 'prev') {
                    e.preventDefault();
                    return showStep(idx - 1);
                }
                if (action === 'next') {
                    e.preventDefault();
                    if (idx >= total - 1) {
                        cleanup();
                        setTourComplete(tourId);
                        return;
                    }
                    return showStep(idx + 1);
                }
                if (action === 'skip') {
                    e.preventDefault();
                    cleanup();
                    setTourComplete(tourId);
                }
            };

            const onKeyDown = (e) => {
                if (e.key === 'Escape') {
                    cleanup();
                    setTourComplete(tourId);
                } else if (e.key === 'ArrowRight' || e.key === 'Enter') {
                    if (idx >= total - 1) {
                        cleanup();
                        setTourComplete(tourId);
                    } else {
                        showStep(idx + 1);
                    }
                } else if (e.key === 'ArrowLeft') {
                    showStep(idx - 1);
                }
            };

            document.addEventListener('click', onDocClick, true);
            document.addEventListener('keydown', onKeyDown, true);

            cleanupFns.push(() => document.removeEventListener('click', onDocClick, true));
            cleanupFns.push(() => document.removeEventListener('keydown', onKeyDown, true));
        };

        if (!el) {
            return skipOrEnd();
        }

        if (el.closest?.('.sidenav-menu')) {
            forceExpandSidebarAncestors(el);
            spotlightTargetEl = el;
            syncSpotlight();
            requestAnimationFrame(syncSpotlight);
            const warmSpotlight = () => syncSpotlight();
            document.addEventListener('scroll', warmSpotlight, true);
            window.addEventListener('resize', warmSpotlight);
            window.setTimeout(() => {
                document.removeEventListener('scroll', warmSpotlight, true);
                window.removeEventListener('resize', warmSpotlight);
                el = resolveTourTarget(step);
                if (!el || !isVisible(el)) {
                    return skipOrEnd();
                }
                presentStep();
            }, 280);
        } else if (!isVisible(el)) {
            return skipOrEnd();
        } else {
            presentStep();
        }
    };

    return {
        start: () => showStep(0),
        cleanup: () => {
            clearStepHandlers();
            cleanup();
        },
    };
}

/** Sidebar walkthrough for Payroll mode (targets use data-cp-nav from Navigation + nav-item). */
function getPayrollSidebarSteps() {
    const side = { placement: 'right' };
    return [
        {
            selectors: ['.sidenav-menu', '#cp-sidebar-menu'],
            title: 'Payroll menu',
            body: 'This sidebar is your starting point. We will walk through each section you have access to. Items with a chevron expand to show more links.',
            placement: 'right',
            tip: 'Hidden menu items are normal — they depend on your role and licence.',
            openLink: false,
        },
        {
            selector: '[data-cp-nav="sidebar-payroll-company-dashboard"]',
            title: 'Dashboard',
            body: 'Opens the company Payroll Dashboard: KPIs, trends, recent payrolls, and quick actions for payroll operations.',
            illustration: 'dashboard',
            ...side,
            tip: 'After a payroll run, use Refresh on the dashboard if KPIs or charts look stale.',
        },
        {
            selector: '[data-cp-nav="sidebar-payroll-my-dashboard"]',
            title: 'My Dashboard',
            body: 'Your personal employee dashboard (when you have access). Use it for your own HR/payroll-related summaries, separate from company-wide payroll admin.',
            ...side,
        },
        {
            selector: '[data-cp-nav="sidebar-payroll-my-earnings"]',
            title: 'My Earnings',
            body: 'As an employee, view how earnings are set up for you (read-only where applicable).',
            ...side,
        },
        {
            selector: '[data-cp-nav="sidebar-payroll-my-deductions"]',
            title: 'My Deductions',
            body: 'See deduction components that apply to your pay.',
            ...side,
        },
        {
            selector: '[data-cp-nav="sidebar-payroll-my-indicators"]',
            title: 'My Indicators',
            body: 'Payroll indicators (flags/values) that affect how your pay is calculated.',
            ...side,
        },
        {
            selector: '[data-cp-nav="sidebar-payroll-my-tax-credits"]',
            title: 'My Tax Credits',
            body: 'Tax credits linked to your profile for statutory calculations.',
            ...side,
        },
        {
            selector: '[data-cp-nav="sidebar-payroll-my-payslips"]',
            title: 'My Payslips',
            body: 'Access your own payslips and history.',
            ...side,
        },
        {
            selector: '[data-cp-nav="sidebar-payroll-my-profile"]',
            title: 'My Profile',
            body: 'Your employee profile and related settings.',
            ...side,
        },
        {
            selector: '[data-cp-nav="sidebar-payroll-master-file"]',
            title: 'Master File',
            body: 'Employee master records—names, jobs, pay setup, and other data payroll depends on. Keep this accurate before processing runs.',
            ...side,
            tip: 'Fixing data here before a run is faster than correcting payslips and reversals afterward.',
        },
        {
            selector: '[data-cp-nav="sidebar-payroll-contracts"]',
            title: 'Contracts',
            body: 'Employment contracts linked to employees; useful for compliance and payroll context.',
            ...side,
        },
        {
            selector: '[data-cp-nav="sidebar-payroll-people"]',
            title: 'People',
            body: 'Organisation structure: designations, departments, grades, locations, and (if enabled) geofences. These drive reporting and often feed payroll rules.',
            ...side,
        },
        {
            selector: '[data-cp-nav="sidebar-payroll-module"]',
            title: 'Payroll',
            body: 'Core payroll workflow. Expand this section for runs, groups, calendar, year-to-date, and reversals.',
            illustration: 'runs',
            ...side,
            tip: 'Many teams follow: Calendar → create run → Process → Approve → Payslips (your rules may differ).',
        },
        {
            selector: '[data-cp-nav="sidebar-payroll-runs"]',
            title: 'Payroll Management',
            body: 'List and manage payroll runs: create a run, open it, process employees, approve, complete, and generate payslips when ready.',
            illustration: 'runs',
            ...side,
            tip: 'Draft payrolls can be changed; after processing, corrections usually follow your approval workflow.',
        },
        {
            selector: '[data-cp-nav="sidebar-payroll-groups"]',
            title: 'Payroll Groups',
            body: 'Define which employees belong to which payroll batch or schedule.',
            ...side,
        },
        {
            selector: '[data-cp-nav="sidebar-payroll-calendar"]',
            title: 'Payroll Calendar',
            body: 'Generate the payroll calendar for tax periods. Many environments require a calendar entry before you can run payroll for that period.',
            ...side,
            tip: 'If a run is blocked for a period, check that the calendar exists for that tax month or year.',
        },
        {
            selector: '[data-cp-nav="sidebar-payroll-ytd"]',
            title: 'YTD Management',
            body: 'Review and adjust year-to-date figures where your process allows—used for statutory reporting and reconciliation.',
            ...side,
        },
        {
            selector: '[data-cp-nav="sidebar-payroll-reverse-calculations"]',
            title: 'Reverse Calculations',
            body: 'Tools to reverse or correct calculated payroll outcomes when your workflow supports it.',
            ...side,
        },
        {
            selector: '[data-cp-nav="sidebar-pay-deductions"]',
            title: 'Pay and Deductions',
            body: 'Company-wide pay components. Expand to manage earnings, deductions, benefits, indicators, and calculation tables that drive payroll logic.',
            illustration: 'settings',
            ...side,
            tip: 'Configure company earning and deduction types here before assigning them to employees.',
        },
        {
            selector: '[data-cp-nav="sidebar-payroll-earnings"]',
            title: 'Earnings',
            body: 'Configure company earning types (basic pay, allowances, etc.) assigned to employees.',
            ...side,
        },
        {
            selector: '[data-cp-nav="sidebar-payroll-deductions"]',
            title: 'Deductions',
            body: 'Configure voluntary deductions at company level.',
            ...side,
        },
        {
            selector: '[data-cp-nav="sidebar-payroll-benefits"]',
            title: 'Benefits',
            body: 'Employee benefits that integrate with payroll.',
            ...side,
        },
        {
            selector: '[data-cp-nav="sidebar-payroll-indicators"]',
            title: 'Indicators',
            body: 'Flags and values that formulas and rules can reference during payroll.',
            ...side,
        },
        {
            selector: '[data-cp-nav="sidebar-payroll-calculation-tables"]',
            title: 'Tables',
            body: 'Calculation tables used by indicators or formulas (e.g. tiered or lookup values).',
            ...side,
        },
        {
            selector: '[data-cp-nav="sidebar-payroll-reports"]',
            title: 'Reports',
            body: 'Payroll and HR reporting: summaries, control reports, costing, GL journals, bank files, and country-specific statutory reports where your tenant is configured for them.',
            illustration: 'dashboard',
            ...side,
        },
        {
            selector: '[data-cp-nav="sidebar-payroll-payslips"]',
            title: 'Payslips',
            body: 'Company payslip management: list, preview, generate, and distribute payslips after payroll is processed.',
            ...side,
        },
        {
            selector: '[data-cp-nav="sidebar-payroll-salary-distribution"]',
            title: 'Salary Distribution',
            body: 'How pay is split across accounts or cost centres where you use this feature.',
            ...side,
        },
        {
            selector: '[data-cp-nav="sidebar-payroll-transaction-input"]',
            title: 'Transaction Input',
            body: 'Bulk or structured input of payroll transactions (e.g. variable pay) for a period.',
            ...side,
            tip: 'Use this when you have period-specific amounts (e.g. overtime or imports) instead of changing core setup each month.',
        },
        {
            selector: '[data-cp-nav="sidebar-payroll-stored-amounts"]',
            title: 'Stored Amounts',
            body: 'Fixed or stored amounts applied in payroll runs (e.g. recurring values) separate from one-off transaction input.',
            ...side,
        },
    ];
}

function getPayrollTours() {
    const sidebar = getPayrollSidebarSteps();
    return {
        'payroll-dashboard': createTour('payroll-dashboard', [
            ...sidebar,
            {
                selector: '[data-payroll-tour-start]',
                title: 'This screen',
                body: 'You are on the Payroll Dashboard. After the sidebar tour, use this page for charts, alerts, and the Process Payroll shortcut.',
                illustration: 'dashboard',
                placement: 'bottom',
                openLink: false,
            },
            {
                selector: '#cp-payroll-dashboard-process-link',
                title: 'Process Payroll',
                body: 'Use this to manage payroll runs — create, process, approve, and complete payrolls.',
                illustration: 'runs',
                placement: 'bottom',
                tip: 'Keyboard shortcut Ctrl+P (Windows/Linux) or ⌘P (Mac) can start a new regular payroll for the current month when enabled.',
            },
            {
                selectors: ['#payrollCostTrendChart', '#payrollTrendsChart'],
                title: 'Trends',
                body: 'This chart summarizes payroll activity over time to help you spot changes quickly.',
                illustration: 'dashboard',
                placement: 'top',
            },
            {
                selector: '#payrollStatusChart',
                title: 'Status distribution',
                body: 'See how many payrolls are pending vs completed at a glance.',
                placement: 'left',
            },
        ]),
        'payroll-index': createTour('payroll-index', [
            ...sidebar,
            {
                selector: '[data-payroll-tour-start]',
                title: 'Payroll Runs list',
                body: 'This page lists payroll runs. Filter, search, then open a run or use Run / Approve / Complete from the grid when available.',
                illustration: 'runs',
                placement: 'bottom',
                openLink: false,
            },
            {
                selector: '#cp-payroll-create-btn',
                title: 'Create Payroll',
                body: 'Start a new payroll run for a pay period. You’ll pick type, pay date, and group/criteria as needed.',
                illustration: 'runs',
                placement: 'bottom',
                tip: 'Choose the correct payroll group and pay date—monthly groups often allow only one regular run per period.',
            },
            {
                selector: '#cp-payroll-search-form input[type="search"]',
                title: 'Search',
                body: 'Search payrolls by name or keywords.',
                illustration: 'default',
                placement: 'bottom',
            },
            {
                selector: '#cp-payroll-index-filters',
                title: 'Filters',
                body: 'Filter by Type, Status, and Period to narrow down results.',
                illustration: 'settings',
                placement: 'bottom',
            },
            {
                selector: '#cp-payroll-table',
                title: 'Payroll list',
                body: 'Open a payroll to view details, process employees, and generate payslips when ready.',
                illustration: 'runs',
                placement: 'top',
            },
            {
                selector: 'button[onclick^="runPayroll("], button[onclick^="approvePayroll("], button[onclick^="rollbackPayroll("]',
                title: 'Quick actions',
                body: 'Contextual buttons appear per payroll status (Run, Approve, Rollback).',
                placement: 'left',
            },
        ]),
        'payroll-create': createTour('payroll-create', [
            ...sidebar,
            {
                selector: '[data-payroll-tour-start]',
                title: 'Create Payroll',
                body: 'Set up a new payroll run: choose period, pay date, type, and any optional settings, then save.',
                illustration: 'runs',
                placement: 'bottom',
                openLink: false,
                tip: 'Weekly groups may ask which week of the month; daily or hourly groups can allow more than one run in a month.',
            },
            {
                selector: '#payrollForm',
                title: 'Payroll form',
                body: 'Fill out the required fields. The payroll name may be generated from the selected year/month.',
                illustration: 'settings',
                placement: 'top',
            },
            {
                selector: '#payDateInput',
                title: 'Pay date',
                body: 'This is the date employees will be paid (often month-end).',
                placement: 'bottom',
            },
            {
                selector: '#yearSelect',
                title: 'Period selection',
                body: 'Pick the Year/Month for the payroll run. This determines the pay period.',
                placement: 'bottom',
            },
        ]),
        'payroll-show': createTour('payroll-show', [
            ...sidebar,
            {
                selector: '[data-payroll-tour-start]',
                title: 'Payroll detail',
                body: 'This is the workspace for one payroll run: run processing, approve, generate payslips, and review employees.',
                illustration: 'runs',
                placement: 'bottom',
                openLink: false,
            },
            {
                selector: '#cp-payroll-actions-header',
                title: 'Actions',
                body: 'Use these actions based on the current payroll status.',
                illustration: 'settings',
                placement: 'bottom',
            },
            {
                selector: 'button[onclick^="runPayroll("]',
                title: 'Run Payroll',
                body: 'Processes employee transactions for this period. If disabled, check payroll calendar and prior-month requirements.',
                placement: 'bottom',
                tip: 'Resolve validation messages (e.g. missing salaries) on this screen before running again.',
            },
            {
                selector: 'button[onclick^="approvePayroll("]',
                title: 'Approve',
                body: 'Approves a processed payroll after review.',
                placement: 'bottom',
            },
            {
                selectors: ['#employee-search-status', '#employee-search-processed', '#employee-search'],
                title: 'Employee list search',
                body: 'Search/filter employees in this payroll to quickly find issues or specific employees.',
                illustration: 'default',
                placement: 'top',
            },
        ]),
    };
}

function inferTourFromLocation() {
    const path = window.location.pathname || '';
    if (path.includes('/company/payroll-dashboard')) return 'payroll-dashboard';
    if (path.match(/\/company\/payroll\/\d+$/)) return 'payroll-show';
    if (path.includes('/company/payroll/create')) return 'payroll-create';
    if (path.includes('/company/payroll')) return 'payroll-index';
    return null;
}

export function initPayrollTour() {
    // Auto-start from query param (e.g. help menu from non-payroll pages)
    try {
        const params = new URLSearchParams(window.location.search || '');
        const requested = params.get('cp_tour');
        if (requested) {
            params.delete('cp_tour');
            const qs = params.toString();
            const cleanUrl = window.location.pathname + (qs ? '?' + qs : '') + window.location.hash;
            window.history.replaceState({}, '', cleanUrl);

            const tours = getPayrollTours();
            tours?.[requested]?.start?.();
        }
    } catch (_) {
        // ignore
    }

    document.addEventListener('click', (e) => {
        const trigger = e.target?.closest?.('[data-payroll-tour-start]');
        if (!trigger) return;
        e.preventDefault();

        const tourKey =
            trigger.getAttribute('data-payroll-tour') ||
            inferTourFromLocation() ||
            'payroll-dashboard';

        const tours = getPayrollTours();
        const tour = tours[tourKey];
        tour?.start?.();
    });
}

